"use client";

import { useEffect, useRef, useState } from 'react';
import Icon from '@/components/ui/AppIcon';

export default function QualityOverQuantitySection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef?.current) {
      observer?.observe(sectionRef?.current);
    }

    return () => observer?.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-24 px-6 bg-gradient-to-b from-muted/10 to-background border-y border-white/5">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <h2
            className={`text-4xl md:text-5xl font-bold tracking-tight text-foreground mb-6 transition-all duration-700 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            Quality Over <span className="text-primary">Quantity</span>
          </h2>
          <p
            className={`text-lg text-muted-foreground font-light transition-all duration-700 delay-100 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            We prioritize meaningful connections, not dopamine hits from endless swiping.
          </p>
        </div>

        {/* Comparison Visual */}
        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {/* Traditional Apps */}
          <div
            className={`glass-card p-8 rounded-3xl transition-all duration-700 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <div className="text-center mb-6">
              <h3 className="text-xl font-bold text-muted-foreground mb-2">Traditional Apps</h3>
              <Icon name="XCircleIcon" size={48} className="text-error mx-auto" />
            </div>
            <div className="space-y-6">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">Daily Matches</span>
                  <span className="text-3xl font-bold text-foreground">300+</span>
                </div>
                <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                  <div className="w-full h-full bg-error" />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">Time Spent Swiping</span>
                  <span className="text-3xl font-bold text-foreground">2hrs</span>
                </div>
                <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                  <div className="w-full h-full bg-error" />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">Quality Conversations</span>
                  <span className="text-3xl font-bold text-foreground">5%</span>
                </div>
                <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                  <div className="w-[5%] h-full bg-error" />
                </div>
              </div>
            </div>
            <div className="mt-6 pt-6 border-t border-white/10 text-center">
              <p className="text-sm text-muted-foreground italic">
                Overwhelming choice paradox leads to burnout
              </p>
            </div>
          </div>

          {/* MeetX */}
          <div
            className={`glass-card p-8 rounded-3xl border-2 border-primary/50 transition-all duration-700 delay-200 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <div className="text-center mb-6">
              <h3 className="text-xl font-bold text-foreground mb-2">MeetX</h3>
              <Icon name="CheckCircleIcon" variant="solid" size={48} className="text-success mx-auto" />
            </div>
            <div className="space-y-6">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">Daily Matches</span>
                  <span className="text-3xl font-bold text-primary">3</span>
                </div>
                <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                  <div className="w-[10%] h-full bg-gradient-to-r from-primary to-secondary" />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">Time Spent Reviewing</span>
                  <span className="text-3xl font-bold text-primary">15min</span>
                </div>
                <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                  <div className="w-[12%] h-full bg-gradient-to-r from-primary to-secondary" />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">Quality Conversations</span>
                  <span className="text-3xl font-bold text-primary">85%</span>
                </div>
                <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                  <div className="w-[85%] h-full bg-gradient-to-r from-primary to-secondary" />
                </div>
              </div>
            </div>
            <div className="mt-6 pt-6 border-t border-white/10 text-center">
              <p className="text-sm text-foreground font-medium">
                Curated matches = Higher success rate
              </p>
            </div>
          </div>
        </div>

        {/* Bottom Stats */}
        <div className="mt-16 text-center">
          <div className="inline-flex items-center gap-2 px-6 py-3 glass-card rounded-full">
            <Icon name="ChartBarIcon" variant="solid" size={20} className="text-primary" />
            <span className="text-foreground font-semibold">
              MeetX users report <span className="text-primary">40% less dating fatigue</span> and <span className="text-primary">3x more meaningful dates</span>
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}